"use client";

import React, { useEffect, useState } from 'react';
import { SlotText } from "slot-text/react";
import "slot-text/style.css";

/**
 * <LiveStat /> - the canonical Delphina widget for live, animated financial
 * counters (e.g. "Assets under guidance"). Encapsulates the slot-machine
 * animation, the placeholder contract, and the accessibility plumbing.
 *
 * Placeholder contract (DEL-563, see DESIGN.md "Unavailable data"):
 *   - If `value` is `null` or `<= 0`, the component renders `null`.
 *     The page never claims a number it cannot back.
 *   - If `value` is a positive number, the component renders the
 *     slot-machine animated counter with `aria-live="polite"` and
 *     `aria-busy` during animation. The animation respects
 *     `prefers-reduced-motion`.
 *
 * @example
 *   <LiveStat
 *     value={assetsUnderGuidance}
 *     label="Assets under guidance"
 *     format="currency"
 *   />
 *
 * @example Round to millions for headline copy
 *   <LiveStat
 *     value={assetsUnderGuidance}
 *     label="Assets under guidance"
 *     format="currency-rounded-m"
 *   />
 *
 * @example Non-animated inline use (trust badge, sidebar sentence)
 *   {assetsUnderGuidance !== null && (
 *     <span className="font-semibold">
 *       {formatLiveStatNumber(assetsUnderGuidance, "currency-rounded-m")}
 *     </span>
 *   )}{' '}
 *   assets under guidance
 */
export type LiveStatFormat = "currency" | "currency-rounded-m" | "plain";

interface LiveStatProps {
  /** The live number to display. `null` or `<= 0` → renders nothing. */
  value: number | null;
  /** Caption under the number. Required so the screen-reader announcement is meaningful. */
  label: string;
  /** Display format. Default: "currency". */
  format?: LiveStatFormat;
  /** Optional extra classes for the outer container. */
  className?: string;
  /** Optional aria-label override; defaults to `label`. */
  ariaLabel?: string;
}

const ANIMATION_TOTAL_MS = 180_000;
const ANIMATION_TICK_MS = 1500;
const ANIMATION_START_OFFSET = 987;
const ANIMATION_DECREASE_EVERY_MS = 20_000;
const ANIMATION_DECREASE_AMOUNT = 7;

const formatNumber = (value: number, format: LiveStatFormat): string => {
  const locale = "en-GB";
  switch (format) {
    case "currency":
      return `£${value.toLocaleString(locale, { maximumFractionDigits: 0 })}`;
    case "currency-rounded-m":
      return `£${Math.floor(value / 1_000_000).toLocaleString(locale)}m+`;
    case "plain":
      return value.toLocaleString(locale, { maximumFractionDigits: 0 });
    default:
      return `£${value.toLocaleString(locale, { maximumFractionDigits: 0 })}`;
  }
};

/**
 * Underlying primitive for non-animated inline use (e.g. the trust-badge
 * line under the home hero, the sidebar copy on /login). Returns `null`
 * when the value is unavailable - the same placeholder contract as the
 * `<LiveStat>` component itself.
 *
 * Call sites must still gate the surrounding JSX on the returned value
 * being non-null (or use a parent null check) so the badge disappears
 * entirely when the API is down.
 *
 * @example
 *   {assetsUnderGuidance !== null && (
 *     <span>{formatLiveStatNumber(assetsUnderGuidance, "currency-rounded-m")}</span>
 *   )}
 */
export const formatLiveStatNumber = (
  value: number | null,
  format: LiveStatFormat = "currency",
): string | null => {
  if (value === null || value <= 0) return null;
  return formatNumber(value, format);
};

/**
 * useReducedMotion polyfill - guard for SSR/test environments where
 * `window.matchMedia` is unavailable. The animation is effectively a no-op
 * when reduced-motion is requested (the value still updates to `endValue`
 * immediately, just without the slot-machine effect).
 */
const usePrefersReducedMotion = (): boolean => {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (!mq || typeof mq.matches !== "boolean") return;
    setReduced(mq.matches);
    const handler = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  return reduced;
};

export default function LiveStat({
  value,
  label,
  format = "currency",
  className,
  ariaLabel,
}: LiveStatProps) {
  const [animatedValue, setAnimatedValue] = useState<number | null>(null);
  const [isAnimationDone, setIsAnimationDone] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();

  // Reset animation whenever the live value changes.
  useEffect(() => {
    if (value === null || value <= 0) {
      setAnimatedValue(null);
      setIsAnimationDone(false);
      return;
    }

    if (prefersReducedMotion) {
      // Skip the slot-machine animation; render the final value immediately.
      setAnimatedValue(value);
      setIsAnimationDone(true);
      return;
    }

    const endValue = value;
    const startValue = endValue - ANIMATION_START_OFFSET;
    const totalUpdates = ANIMATION_TOTAL_MS / ANIMATION_TICK_MS;
    const decreaseCount = Math.floor(
      (ANIMATION_TOTAL_MS - ANIMATION_TICK_MS) / ANIMATION_DECREASE_EVERY_MS,
    );
    const normalUpdateCount = totalUpdates - decreaseCount;
    const totalDecrease = decreaseCount * ANIMATION_DECREASE_AMOUNT;
    const baseIncrement =
      (endValue - startValue + totalDecrease) / normalUpdateCount;
    const multipliers = [1.0, 0.5, 1.5];

    setAnimatedValue(startValue);
    setIsAnimationDone(false);

    const startTime = Date.now();
    let currentValue = startValue;
    let updateCount = 0;
    let lastBucket = 0;

    const interval = setInterval(() => {
      updateCount += 1;

      if (updateCount >= totalUpdates) {
        setAnimatedValue(endValue);
        setIsAnimationDone(true);
        clearInterval(interval);
        return;
      }

      const elapsed = Date.now() - startTime;
      const currentBucket = Math.floor(elapsed / ANIMATION_DECREASE_EVERY_MS);
      const isDecreaseTick = currentBucket > lastBucket;
      lastBucket = currentBucket;

      if (isDecreaseTick) {
        currentValue -= ANIMATION_DECREASE_AMOUNT;
      } else {
        const multiplier = multipliers[(updateCount - 1) % multipliers.length];
        const rawIncrement = baseIncrement * multiplier;
        const roundedIncrement = Math.round(rawIncrement);
        const oddIncrement =
          roundedIncrement % 2 === 0 ? roundedIncrement + 1 : roundedIncrement;
        currentValue += oddIncrement;
      }

      currentValue = Math.min(currentValue, endValue);
      setAnimatedValue(currentValue);
    }, ANIMATION_TICK_MS);

    return () => clearInterval(interval);
  }, [value, prefersReducedMotion]);

  // Placeholder contract: render nothing when the live value is missing.
  if (value === null || value <= 0) return null;

  const displayValue =
    animatedValue !== null
      ? formatNumber(animatedValue, format)
      : formatNumber(value - ANIMATION_START_OFFSET, format);

  return (
    <div
      className={className}
      role="status"
      aria-live="polite"
      aria-busy={!isAnimationDone}
      aria-label={ariaLabel ?? label}
    >
      <p className="text-3xl md:text-4xl font-bold font-mono text-positive-600">
        <SlotText
          text={displayValue}
          options={{ direction: "up", skipUnchanged: true, stagger: 0, duration: 1200 }}
        />
      </p>
      <p className="text-sm text-gray-500">{label}</p>
    </div>
  );
}